/**
 * Quantity-mode (MDOT-24700) AUM dialog: lets the user change the fixed AUM budget the
 * quantities are held against — the "raise the portfolio AUM" recovery path out of negative
 * cash (I4). Spawned from {@link NegativeCashBanner}'s quick action; the confirmed value is
 * committed through the quantity builder's `setAum`.
 */
import { MonetaryInput } from "$root/components/monetary-input";
import { formatCurrencyAmount } from "$root/utils/currency";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { labAdd, truncateToPrecision } from "$root/utils/monetary-math";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { Button, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { ActionText, Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useState } from "react";
import { minimumAumToCoverCash } from "./derive";

/**
 * Cap on the user-typed AUM. IEEE-754 doubles keep monetary math reliable up to ~$10T (see
 * `FLOAT_SIGNIFICANT_DIGITS` in `$root/utils/monetary-math`) — same rationale as the
 * portfolio lab's `MAX_LAB_AUM`, defined locally so this feature stays self-contained.
 */
export const MAX_QUANTITY_AUM = 1e13;

type QuantityAumDialogProps = {
	currentAum: number | null;
	/**
	 * Absolute value of the negative cash the user is recovering from (I4). When > 0 the
	 * input is pre-filled with the minimum AUM that covers it (current AUM + missing cash,
	 * ceiled to cents) so the user confirms instead of computing the sum by hand, and a
	 * quick action restores that minimum if they type less.
	 */
	missingCash?: number;
	/** Currency of every monetary amount; the platform default when unset. */
	currency?: Currencies | null;
	onConfirm(newAum: number): void;
};

function QuantityAumDialog({
	show,
	onClose,
	onAnimationStateChange,
	currentAum,
	missingCash,
	currency,
	onConfirm,
}: QuantityAumDialogProps & MinimumDialogProps) {
	const coveringCash = missingCash != null && missingCash > 0;
	const minimumAum = coveringCash ? minimumAumToCoverCash(currentAum ?? 0, missingCash) : null;
	const [value, setValue] = useState<number | null>(minimumAum ?? currentAum);
	const exceedsMax = value != null && value > MAX_QUANTITY_AUM;
	// The budget must be strictly positive: derived weights are meaningless on AUM ≤ 0 and
	// the builder's `setAum` refuses it anyway.
	const invalid = value == null || Number.isNaN(value) || value <= 0 || exceedsMax;
	// Compare against the EXACT requirement, not the cent-ceiled suggestion: a value inside
	// the sub-cent window between the two already brings cash back to ≥ 0.
	const stillNegative = coveringCash && value != null && !invalid && value < labAdd(currentAum ?? 0, missingCash);

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header="Portfolio AUM"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							Cancel
						</Button>
					}
					primaryAction={
						<SubmitButton disabled={invalid} data-qualifier="CompositionEditor/AumDialog/Confirm">
							Confirm
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => {
				if (value != null) {
					onConfirm(value);
				}
			}}
		>
			<Text type="Body/M/Book" className="mb-4" as="p">
				Set the portfolio AUM — the fixed budget the quantities are held against. Raising it adds the difference to
				cash.
			</Text>
			{minimumAum != null && (
				<Text type="Body/M/Book" className="mb-4" as="p">
					Pre-filled with the minimum that brings cash back to zero: current AUM{" "}
					<strong>{formatCurrencyAmount(truncateToPrecision(currentAum ?? 0, 2), currency)}</strong> + missing cash{" "}
					<strong>{formatCurrencyAmount(truncateToPrecision(missingCash ?? 0, 2), currency)}</strong>.
				</Text>
			)}
			<FormField
				label=""
				error={
					exceedsMax
						? `The portfolio AUM cannot exceed ${formatCurrencyAmount(MAX_QUANTITY_AUM, currency, 0)}`
						: undefined
				}
			>
				<MonetaryInput
					value={value}
					onChange={setValue}
					min={0}
					max={MAX_QUANTITY_AUM}
					currency={currency}
					data-qualifier="CompositionEditor/AumDialog/Input"
				/>
			</FormField>
			{stillNegative && minimumAum != null && (
				<Text type="Body/M/Book" className="mt-2" as="p">
					This amount still leaves cash negative —{" "}
					<ActionText data-qualifier="CompositionEditor/AumDialog/UseMinimum" onClick={() => setValue(minimumAum)}>
						use the minimum ({formatCurrencyAmount(minimumAum, currency)})
					</ActionText>
					.
				</Text>
			)}
		</Dialog>
	);
}

export function spawnQuantityAumDialog(props: QuantityAumDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<QuantityAumDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onConfirm={(newAum) => {
					props.onConfirm(newAum);
					resolve();
				}}
			/>
		)),
	);
}
